import React, { lazy, Suspense, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Plus, X } from '@phosphor-icons/react';
import Icon from '../icons/Icon';
import useTerminalTab from '../../hooks/useTerminalTab';
import { TERMINAL_TARGETS, sandboxOwnerLabel, terminalTarget } from '../../services/TerminalService';
import './Terminal.css';

// xterm.js only loads once the tab opens: it stays out of the main bundle (as in the Code view's dock).
const TerminalPanel = lazy(() => import('./TerminalPanel'));

const labelOf = (target) => TERMINAL_TARGETS.find((t) => t.id === target)?.label || target;

/** "Workspace", "Workspace 2", …: the sub-tab names, numbered per container. */
function namesOf(sessions) {
  const seen = {};
  return sessions.map((session) => {
    seen[session.target] = (seen[session.target] || 0) + 1;
    return seen[session.target] > 1 ? `${labelOf(session.target)} ${seen[session.target]}` : labelOf(session.target);
  });
}

/**
 * The project's Terminal tab (utils/projectTabsModel.js): the same TerminalPanel as the Code view's dock, full height,
 * with sub-tabs, one shell each. "+" opens another one with a container picker: Workspace (the project's folder, as
 * the dock), Preview runner or vfs-service (the sandbox's system containers: only what the server's `targets` allow,
 * the others greyed with why). Each shell lives while its sub-tab is open, also while another project tab is shown
 * (`shown` false: WorkspaceView keeps this mounted, hidden, until the person leaves the project); closing a sub-tab
 * ends its shell. The tab itself is only offered where the person may open a terminal (hooks/useTerminalTab.js).
 */
export default function TerminalTabView({ projectId, shown = true }) {
  const { ready, info } = useTerminalTab(projectId);
  const nextKey = useRef(2);
  const [sessions, setSessions] = useState([{ key: 1, target: 'workspace' }]);
  const [activeKey, setActiveKey] = useState(1);
  const [picking, setPicking] = useState(false);
  const pickerRef = useRef(null);

  useEffect(() => {
    if (!picking) { return undefined; }
    const away = (event) => { if (!pickerRef.current?.contains(event.target)) { setPicking(false); } };
    const escape = (event) => { if (event.key === 'Escape') { setPicking(false); } };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', escape);
    };
  }, [picking]);

  if (!ready) { return <div className="flow-terminal-connecting is-inline" role="status">{'Loading the terminal…'}</div>; }
  if (!info?.available) {
    return <div className="code-terminal-unavailable" role="status">{info?.message || 'The terminal is not available for this project.'}</div>;
  }

  const maxSessions = Number(info.maxSessions) > 0 ? Number(info.maxSessions) : null;
  const full = maxSessions !== null && sessions.length >= maxSessions;
  const names = namesOf(sessions);
  const owner = sandboxOwnerLabel(info);

  const add = (target) => {
    const key = nextKey.current;
    nextKey.current += 1;
    setSessions((list) => [...list, { key, target }]);
    setActiveKey(key);
    setPicking(false);
  };
  const close = (key) => {
    const index = sessions.findIndex((s) => s.key === key);
    const rest = sessions.filter((s) => s.key !== key);
    setSessions(rest);
    if (key === activeKey) { setActiveKey(rest[Math.max(0, index - 1)]?.key ?? null); }
  };

  return (
    <div className="terminal-tab-view" data-testid="terminal-tab-view">
      <div className="code-terminal-header terminal-tab-header">
        <div aria-label="Terminals" className="code-terminal-tabs" role="tablist">
          {sessions.map((session, i) => (
            <span className={`terminal-subtab${session.key === activeKey ? ' active' : ''}`} key={session.key}>
              <button
                aria-controls={`terminal-panel-${session.key}`}
                aria-selected={session.key === activeKey}
                className={`code-terminal-tab${session.key === activeKey ? ' active' : ''}`}
                data-testid={`terminal-subtab-${session.key}`}
                id={`terminal-subtab-${session.key}`}
                onClick={() => setActiveKey(session.key)}
                role="tab"
                type="button"
              >
                <Icon name="terminal" size={14} />
                <span>{names[i]}</span>
              </button>
              <button
                aria-label={`Close ${names[i]}`}
                className="terminal-subtab-close"
                data-testid={`terminal-subtab-close-${session.key}`}
                onClick={() => close(session.key)}
                title="Close this terminal (its shell ends)"
                type="button"
              >
                <X aria-hidden size={12} />
              </button>
            </span>
          ))}
        </div>
        <div className="terminal-tab-add" ref={pickerRef}>
          <button
            aria-expanded={picking}
            aria-haspopup="menu"
            aria-label="Open another terminal"
            className="code-terminal-toggle terminal-tab-plus"
            data-testid="terminal-add"
            disabled={full}
            onClick={() => setPicking((on) => !on)}
            title={full ? `At most ${maxSessions} terminals on this project at once. Close one first.` : 'Open another terminal'}
            type="button"
          >
            <Plus aria-hidden size={14} />
          </button>
          {picking && (
            <div aria-label="Where the terminal runs" className="terminal-target-menu" data-testid="terminal-target-menu" role="menu">
              {TERMINAL_TARGETS.map((target) => {
                const access = terminalTarget(info, target.id);
                return (
                  <button
                    aria-disabled={!access.available}
                    className="terminal-target-item"
                    data-testid={`terminal-target-${target.id}`}
                    disabled={!access.available}
                    key={target.id}
                    onClick={() => add(target.id)}
                    role="menuitem"
                    title={access.message || undefined}
                    type="button"
                  >
                    <strong>{target.label}</strong>
                    <span>{access.available ? target.desc : access.message || 'Not available'}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        {owner && <span className="code-terminal-owner" title="Commands run in the project owner's sandbox, with its limits">{owner}</span>}
      </div>
      <div className="terminal-tab-body">
        {sessions.length === 0 && (
          <div className="code-terminal-unavailable" role="status">
            {'No terminal open. '}
            <button className="terminal-tab-open" onClick={() => add('workspace')} type="button">{'Open one in the workspace'}</button>
          </div>
        )}
        {sessions.map((session) => (
          <div
            aria-labelledby={`terminal-subtab-${session.key}`}
            className="code-terminal-body"
            hidden={session.key !== activeKey}
            id={`terminal-panel-${session.key}`}
            key={session.key}
            role="tabpanel"
          >
            <Suspense fallback={<div className="flow-terminal-connecting" role="status">{'Loading the terminal…'}</div>}>
              <TerminalPanel active={shown && session.key === activeKey} idleMinutes={info.idleMinutes ?? null} projectId={projectId} target={session.target} />
            </Suspense>
          </div>
        ))}
      </div>
    </div>
  );
}

TerminalTabView.propTypes = {
  projectId: PropTypes.string.isRequired,
  // false while another project tab is shown: the shells keep running, none takes the keyboard
  shown: PropTypes.bool,
};
